import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import PaginationControls from '../../utils/PaginationControls';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const FormTeamsList = ({ onSelectTeam, selectedTeamId, apiBaseUrl, fabricAccessLevel }) => {
  // Teams data
  const [teams, setTeams] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Pagination and search states
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const navigate = useNavigate();

  // API base URL
  const API_BASE_URL = '/pceapi/fabrics';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Fetch teams on component mount and when pagination, search, or fabric access level changes
  useEffect(() => {
    fetchTeams();
  }, [page, size, search, fabricAccessLevel]);

  // Fetch teams from API
  const fetchTeams = async () => {
      setIsProcessingPopupVisible(true);
      setProcessingMessage('Loading teams');
    
    try {
      const fullUrl = `${apiBaseUrl}/teams/list`;
      const response = await axios.post(
      fullUrl,
        {
          page,
          size,
          search: search || undefined
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load teams');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setTeams(data.data || []);
      setTotal(data.total || 0);
      
    } catch (error) {
      console.error('Error fetching teams:', error);
      setPopupErrorMessage(error.message || 'Failed to load teams');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  
  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();   // stop form submission
      handleSearch();
    }
  };
  
  // Handle team selection
  const handleSelectTeam = (teamId) => {
    if (!onSelectTeam) return;
    if (selectedTeamId === teamId) {
      onSelectTeam(null, null);
      return;
    }
    const team = teams.find(t => t.id === teamId);
    onSelectTeam(teamId, team?.name || null);
  };

  // Filter teams: when fabric is PRIVATE, hide PUBLIC teams to prevent
  // leaking a PRIVATE project into a team where anyone can join.
  const visibleTeams = fabricAccessLevel === 'PRIVATE'
    ? teams.filter(t => t.team_access_level !== 'PUBLIC')
    : teams;

  // Auto-deselect: if the currently selected team is no longer visible
  // (e.g. user toggled fabric to PRIVATE while a PUBLIC team was selected),
  // clear the selection so the form doesn't carry a hidden value.
  // Auto-deselect only when the selected team is present in the fetched page
  // but hidden by the access-level filter (e.g. fabric switched to PRIVATE
  // while a PUBLIC team was selected).
  useEffect(() => {
    if (!selectedTeamId) return;
    const inFetched = teams.find(t => t.id === selectedTeamId);
    if (!inFetched) return;
    const isVisible = visibleTeams.some(t => t.id === selectedTeamId);
    if (!isVisible && onSelectTeam) {
      onSelectTeam(null);
    }
  }, [fabricAccessLevel, teams]);
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  return (
    <div  className="ev-card-3"> 
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="col-5">
          <div className= "position-relative">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search teams"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
            <button 
              type="button"
              className="ev-btn-lightblue-fill-round search-bar-button" 
              onClick={handleSearch}
            >
              <i className="bi bi-search"></i>
            </button>
          </div>   
        </div>
        <div className="col-7 d-flex justify-content-end align-items-center" >
          <div className="d-flex align-items-center">
            <button 
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            <span className="ev-xsmall-p-dark ms-1">
              Page {page}/{Math.ceil(total / size) || 1}
            </span>

            <button 
              type="button"
              className="ev-btn-light-fill  ev-btn-xsmall ms-1" 
              disabled={page >= Math.ceil(total / size)}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
     
        <div className="d-flex align-items-center ms-2" >
          <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
        </div>
       </div>
      </div>
      
      <div className=" scrollable-element vh-10">
        
        {visibleTeams.length === 0 ? (
          <p className="ev-small-p-blue text-center mt-2">No teams found</p>
        ) : (
          <div>
            {visibleTeams.map(team => (
              <div 
                key={team.id} 
                className={`mb-0 ev-padding-small ev-xsmall-p-blue cursor-pointer  mb-1 ev-bold-med ${selectedTeamId === team.id ? 'ev-card-1 ev-radius-small list-element-selected' : ''}`}
                onClick={() => handleSelectTeam(team.id)}
              >
                <i className="bi bi-people"></i>&nbsp; {team.name}
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        </div>
      
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
    </div>
  );
};

export default FormTeamsList;